<template>
  <div>
    <div class="info-block px-3 py-1 mb-10 d-flex align-center">
      <v-icon size="24" icon="$warning" />
      <span class="subtitle2 ml-2" v-html="$t('modals.exportTickets.disclaimer')" />
    </div>
    <div class="subtitle1 mb-1">
      {{ $t("modals.exportTickets.subtitle") }}
    </div>
    <v-radio-group v-model="localValue.type" hide-details>
      <v-radio
        v-for="choice in [ExportChoice.ALL, ExportChoice.SPECIFIC]"
        :key="choice"
        class="mb-3"
        :value="choice"
        :ripple="false"
        :label="$t(`modals.exportTickets.choices.${choice}`)"
      ></v-radio>
    </v-radio-group>
    <tickets-filters
      v-model="localValue.filters"
      :disabled="localValue.type === ExportChoice.ALL"
      :date-range-presets="presets"
      :teleport-date-picker="true"
      is-vertical
      class="mt-2"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { type ExportDialogPayload } from "@/_store/exports.module";
import { defaultFiltersState, type TicketsFilters } from "@/_store/filters.module";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { useI18n } from "vue-i18n";
import { ExportChoice } from "@/constants/exports.ts";
import TicketsFiltersComponent from "@/components/tickets/TicketsFilters.vue";
import moment from "moment";

export default defineComponent({
  components: { TicketsFilters: TicketsFiltersComponent },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
  },
  setup(_, { emit }) {
    const i18n = useI18n();

    const presets = computed(() => [
      {
        label: i18n.t("dateRangePicker.today"),
        value: [moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
      },
      {
        label: i18n.t("dateRangePicker.yesterday"),
        value: [
          moment().subtract(1, "days").format("YYYY-MM-DD"),
          moment().subtract(1, "days").format("YYYY-MM-DD"),
        ],
      },
      {
        label: i18n.t("dateRangePicker.lastSevenDays"),
        value: [moment().subtract(7, "days").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
      },
      {
        label: i18n.t("dateRangePicker.lastThirtyDays"),
        value: [moment().subtract(30, "days").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
      },
      {
        label: i18n.t("dateRangePicker.lastNinetyDays"),
        value: [moment().subtract(90, "days").format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")],
      },
    ]);

    const localValue = ref<ExportDialogPayload<TicketsFilters>>({
      type: ExportChoice.ALL,
      filters: {
        ...defaultFiltersState.ticketsFilters,
      },
    });

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true, immediate: true }
    );

    return {
      localValue,
      ExportChoice,
      presets,
    };
  },
});
</script>

<style lang="scss" scoped>
:deep(.clear-btn) {
  display: none !important;
}
</style>
